import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    computed,
    inject,
    linkedSignal,
    signal,
    viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonListHeader,
    IonSearchbar,
    IonSpinner,
    ModalController,
    SearchbarCustomEvent,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { searchOutline } from 'ionicons/icons';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { ParametersService } from '@app/components/parameters/parameters.service';
import { ProductRangeService } from '@app/components/poetpro/productrange/productrange.service';
import { ProductSearchService } from './product-search.service';
import {
    PRODUCT_SEARCH_DEBOUNCE_MS,
    PRODUCT_SEARCH_MIN_LENGTH,
    PRODUCT_SEARCH_SUGGESTION_LIMIT,
    ProductSearchResult,
    emptyGroupedResults,
    idleSearchState,
} from './product-search.models';
import { ProductSearchResultItemComponent } from './product-search-result-item.component';
import { ProductSearchMobileModalComponent } from './product-search-mobile-modal.component';

@Component({
    selector: 'app-product-search',
    templateUrl: './product-search.component.html',
    styleUrl: './product-search.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslateModule,
        IonSearchbar,
        IonList,
        IonListHeader,
        IonItem,
        IonLabel,
        IonIcon,
        IonSpinner,
        ProductSearchResultItemComponent,
    ],
    host: {
        '(document:click)': 'onDocumentClick($event)',
        '(window:resize)': 'onResize()',
    },
})
export class ProductSearchComponent {
    private readonly searchService = inject(ProductSearchService);
    private readonly productRangeService = inject(ProductRangeService);
    private readonly parameterService = inject(ParametersService);
    private readonly router = inject(Router);
    private readonly modalCtrl = inject(ModalController);
    private readonly host = inject(ElementRef<HTMLElement>);

    readonly searchbar = viewChild<IonSearchbar>('searchbar');

    readonly query = signal('');
    readonly panelDismissed = signal(false);
    readonly isMobileViewport = signal(this.checkMobile());
    private mobileModalOpen = false;

    private readonly locale = toSignal(
        this.parameterService.getParametersObservable().pipe(
            map(params => params.user?.locale ?? params.country?.code)
        )
    );

    private readonly flatTree = this.productRangeService.flatPhTree(this.locale);

    private readonly debouncedQuery = toSignal(
        toObservable(this.query).pipe(
            map(q => q.trim()),
            distinctUntilChanged(),
            debounceTime(PRODUCT_SEARCH_DEBOUNCE_MS)
        ),
        { initialValue: '' }
    );

    readonly state = computed(() => {
        const query = this.debouncedQuery();
        if (query.length < PRODUCT_SEARCH_MIN_LENGTH) {
            return idleSearchState(query);
        }
        if (this.flatTree.error()) {
            return { status: 'error' as const, query, results: emptyGroupedResults(query) };
        }
        if (!this.flatTree.hasValue()) {
            return { status: 'loading' as const, query, results: emptyGroupedResults(query) };
        }
        return this.searchService.toState(this.flatTree.value(), query, PRODUCT_SEARCH_SUGGESTION_LIMIT);
    });

    readonly activeIndex = linkedSignal({
        source: this.state,
        computation: () => -1,
    });

    readonly flatOptions = computed(() => {
        const { machines, variants } = this.state().results;
        return [...machines, ...variants];
    });

    readonly showPanel = computed(
        () => !this.isMobileViewport() && !this.panelDismissed() && this.state().status !== 'idle'
    );

    readonly showAllActive = computed(() => this.activeIndex() === this.flatOptions().length);

    constructor() {
        addIcons({ searchOutline });
    }

    onDocumentClick(event: MouseEvent): void {
        if (!this.host.nativeElement.contains(event.target as Node)) {
            this.closePanel();
        }
    }

    onResize(): void {
        this.isMobileViewport.set(this.checkMobile());
    }

    onIonInput(event: SearchbarCustomEvent): void {
        const value = event.detail.value ?? '';
        this.query.set(value);
        this.panelDismissed.set(false);
    }

    onFocus(): void {
        // On mobile the modal opens from click so focus-restore after dismiss cannot reopen it.
        if (this.checkMobile()) {
            return;
        }
        if (this.state().status !== 'idle') {
            this.panelDismissed.set(false);
        }
    }

    onSearchbarClick(): void {
        if (!this.checkMobile() || this.mobileModalOpen) {
            return;
        }
        void this.openMobileModal();
    }

    onSearchButtonClick(): void {
        if (this.query().trim().length >= PRODUCT_SEARCH_MIN_LENGTH) {
            this.showAllResults();
        } else if (this.checkMobile()) {
            this.onSearchbarClick();
        } else {
            this.focusInput();
        }
    }

    clearQuery(): void {
        this.query.set('');
        this.panelDismissed.set(true);
        this.focusInput();
    }

    closePanel(): void {
        this.panelDismissed.set(true);
    }

    onKeydown(event: KeyboardEvent): void {
        if (this.isMobileViewport()) {
            return;
        }

        const options = this.flatOptions();
        const includeShowAll =
            this.state().status === 'results' || this.state().status === 'empty';
        const maxIndex = includeShowAll ? options.length : options.length - 1;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.panelDismissed.set(false);
                this.activeIndex.set(Math.min(this.activeIndex() + 1, Math.max(maxIndex, 0)));
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.activeIndex.set(Math.max(this.activeIndex() - 1, -1));
                break;
            case 'Enter':
                event.preventDefault();
                this.activateCurrent();
                break;
            case 'Escape':
                event.preventDefault();
                this.closePanel();
                this.focusInput();
                break;
        }
    }

    onResultSelected(result: ProductSearchResult): void {
        this.closePanel();
        this.router.navigate(result.routerLink, { queryParams: result.queryParams });
    }

    showAllResults(): void {
        const q = this.query().trim();
        if (!q) {
            return;
        }
        this.closePanel();
        this.router.navigate(['/products', 'search'], { queryParams: { query: q } });
    }

    private activateCurrent(): void {
        const options = this.flatOptions();
        const index = this.activeIndex();
        if (index >= 0 && index < options.length) {
            this.onResultSelected(options[index]);
            return;
        }
        if (
            index === options.length ||
            (index < 0 && this.query().trim().length >= PRODUCT_SEARCH_MIN_LENGTH)
        ) {
            this.showAllResults();
        }
    }

    private async openMobileModal(): Promise<void> {
        this.mobileModalOpen = true;
        try {
            const pageInput = await this.searchbar()?.getInputElement();
            pageInput?.blur();

            const modal = await this.modalCtrl.create({
                component: ProductSearchMobileModalComponent,
                componentProps: { initialQuery: this.query() },
                cssClass: 'product-search-mobile-modal',
            });
            await modal.present();
            const { data } = await modal.onWillDismiss<{ query?: string }>();
            if (data?.query != null) {
                this.query.set(data.query);
            }
        } finally {
            this.mobileModalOpen = false;
            const pageInput = await this.searchbar()?.getInputElement();
            pageInput?.blur();
        }
    }

    private focusInput(): void {
        queueMicrotask(() => void this.searchbar()?.setFocus());
    }

    private checkMobile(): boolean {
        return globalThis.matchMedia('(max-width: 991.98px)').matches;
    }
}
